import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CaretDown, CaretRight, CheckCircle, ShieldSlash, ArrowCounterClockwise } from "@phosphor-icons/react";

const STATE_STYLE = {
  acknowledged: "text-emerald-800 bg-emerald-50", outstanding: "text-amber-800 bg-amber-50",
  overdue: "text-red-800 bg-red-50", awaiting_clarification: "text-slate-700 bg-slate-100",
};

// Collapsible "RAMS & Policies" compliance group. Reused inside the Training Matrix (collapsed)
// and on the standalone /policy-matrix page (expanded). Counts overlap and are never additive.
export default function PolicyMatrixGroup({ defaultOpen = false, title = "RAMS & Policies" }) {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("rams_manager", "administrator", "super_admin");
  const [groupOpen, setGroupOpen] = useState(defaultOpen);
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState({});

  const load = useCallback(() => {
    api.get("/policies/matrix").then((r) => setRows(r.data.people)).catch(() => setRows([]));
  }, []);
  useEffect(() => { if (groupOpen && rows === null) load(); }, [groupOpen, rows, load]);

  const remove = async (rid, pid) => {
    const reason = window.prompt("Reason this document is not applicable to this person?");
    if (!reason) return;
    try { await api.post(`/policies/${rid}/exceptions`, { profile_id: pid, reason, scope: "all_sources" }); toast.success("Marked not applicable"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };
  const reinstate = async (eid) => { await api.post(`/policies/exceptions/${eid}/reinstate`); toast.success("Reinstated"); load(); };

  const roll = (rows || []).reduce((a, p) => {
    a.o += p.counts.outstanding; a.od += p.counts.overdue; a.aw += p.counts.awaiting_clarification; a.ncw += p.counts.non_compliant_for_work; return a;
  }, { o: 0, od: 0, aw: 0, ncw: 0 });

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="policy-matrix-group">
      <button data-testid="policy-group-toggle" onClick={() => setGroupOpen((v) => !v)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left">
        {groupOpen ? <CaretDown size={16} className="text-slate-400" /> : <CaretRight size={16} className="text-slate-400" />}
        <span className="font-display font-black text-slate-800 flex-1">{title}</span>
        {rows && (<span className="flex items-center gap-3 text-xs font-semibold">
          {roll.od > 0 && <span className="text-red-800">{roll.od} overdue</span>}
          <span className="text-amber-800">{roll.o} outstanding</span>
          {roll.aw > 0 && <span className="text-slate-600">{roll.aw} awaiting</span>}
          {roll.ncw > 0 && <span className="text-red-800">{roll.ncw} non-compliant for work</span>}
        </span>)}
      </button>
      {groupOpen && (
        <div className="border-t border-slate-100">
          {rows === null ? <p className="text-slate-400 text-sm px-4 py-3">Loading…</p> : (
            <div className="divide-y divide-slate-100" data-testid="policy-matrix">
              {rows.map((p) => {
                const isOpen = open[p.profile_id]; const c = p.counts;
                return (
                  <div key={p.profile_id} data-testid={`matrix-person-${p.profile_id}`}>
                    <button onClick={() => setOpen((o) => ({ ...o, [p.profile_id]: !isOpen }))} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left">
                      {isOpen ? <CaretDown size={14} className="text-slate-400" /> : <CaretRight size={14} className="text-slate-400" />}
                      <span className="font-medium text-slate-800 text-sm flex-1">{p.name}</span>
                      {c.overdue > 0 && <span className="text-xs font-semibold text-red-800">{c.overdue} overdue</span>}
                      {c.outstanding > 0 && <span className="text-xs font-semibold text-amber-800">{c.outstanding} outstanding</span>}
                      {c.awaiting_clarification > 0 && <span className="text-xs font-semibold text-slate-600">{c.awaiting_clarification} awaiting</span>}
                      {c.outstanding === 0 && c.overdue === 0 && <span className="text-xs font-semibold text-emerald-800 inline-flex items-center gap-1"><CheckCircle size={13} weight="fill" /> compliant</span>}
                    </button>
                    {isOpen && (
                      <div className="pl-8 pr-4 pb-3" data-testid={`matrix-items-${p.profile_id}`}>
                        {p.items.length === 0 && <p className="text-sm text-slate-400 py-1">No applicable documents.</p>}
                        {["policy", "rams"].map((cls) => {
                          const list = p.items.filter((it) => (it.doc_class || "policy") === cls);
                          if (!list.length) return null;
                          return (
                            <div key={cls} className="mb-1" data-testid={`matrix-class-${cls}-${p.profile_id}`}>
                              <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mt-2 mb-0.5">{cls === "rams" ? "RAMS" : "Policies & Procedures"} <span className="text-slate-300">· {list.length}</span></div>
                              {list.map((it) => (
                                <div key={it.rams_id} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-50">
                                  <div className="min-w-0">
                                    <Link to={`/policies/${it.rams_id}`} className="text-sm text-slate-800 hover:text-emerald-800 truncate block">{it.title}</Link>
                                    <div className="text-[11px] text-slate-400">{it.sources?.join(" · ")}{it.before_work ? " · before work" : ""}{it.deadline ? ` · due ${it.deadline}` : ""}</div>
                                  </div>
                                  <div className="flex items-center gap-2 shrink-0">
                                    <span className={`text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${STATE_STYLE[it.state] || "bg-slate-100 text-slate-600"}`}>{it.state.replace(/_/g, " ")}</span>
                                    {canManage && <button data-testid={`remove-${it.rams_id}-${p.profile_id}`} onClick={() => remove(it.rams_id, p.profile_id)} title="Mark not applicable" className="text-slate-300 hover:text-red-600"><ShieldSlash size={15} /></button>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })}
                        {p.exceptions?.length > 0 && (
                          <div className="mt-2 pt-2">
                            <div className="text-[10px] font-bold uppercase text-slate-400 mb-1">Not applicable (excluded from counts)</div>
                            {p.exceptions.map((e) => (
                              <div key={e.id} className="flex items-center justify-between gap-3 py-1 text-sm">
                                <span className="text-slate-500 truncate">{e.title} <span className="text-[11px]">· {e.scope === "one_source" ? "one source" : "whole document"} · {e.reason}</span></span>
                                {canManage && <button data-testid={`reinstate-${e.id}`} onClick={() => reinstate(e.id)} className="text-xs font-semibold text-emerald-700 hover:underline inline-flex items-center gap-1"><ArrowCounterClockwise size={12} /> Reinstate</button>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
